import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSessionToken, setSessionToken, clearSessionToken, SESSION_TOKEN_KEY } from '../utils/sessionStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Re-export getSessionToken for backwards compatibility
export { getSessionToken };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Sync user to backend - creates user if they don't exist
  const syncUserToBackend = async (userData) => {
    try {
      console.log('Syncing user to backend:', userData.email);
      const response = await fetch(`${API_URL}/api/auth/sync-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: userData.email,
          name: userData.name,
          picture: userData.picture
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.session_token) {
          setSessionToken(data.session_token);
          console.log('User synced and session token stored');
        }
        if (data.user) {
          setUser(data.user);
        }
        return { success: true, user: data.user, session_token: data.session_token };
      }
      console.log('Failed to sync user:', response.status);
      return { success: false };
    } catch (err) {
      console.error('User sync error:', err);
      return { success: false, error: err.message };
    }
  };

  // Check if user is authenticated
  const checkAuth = useCallback(async () => {
    try {
      const sessionToken = getSessionToken();
      const headers = {};
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // If user is authenticated but we don't have a session token in localStorage,
        // request a new one from the backend
        if (!sessionToken) {
          console.log('User authenticated but no local session token, requesting refresh...');
          refreshSessionToken();
        }
      } else {
        setUser(null);
        clearSessionToken();
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Request a fresh session token from the backend
  const refreshSessionToken = async () => {
    try {
      console.log('Attempting to refresh session token...');
      const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.session_token) {
          setSessionToken(data.session_token);
          console.log('Session token refreshed and stored successfully');
          return true;
        }
      }
      console.log('Failed to refresh session token');
      return false;
    } catch (err) {
      console.error('Token refresh error:', err);
      return false;
    }
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Custom Google OAuth - handles the credential from GoogleLogin component
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogleCredential = async (credential) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Google authentication failed');
      }

      // Store session token in localStorage for cross-origin requests
      if (data.session_token) {
        setSessionToken(data.session_token);
      }

      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Legacy Google OAuth login (kept for backwards compatibility with AuthCallback)
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Email/Password login
  const loginWithEmail = async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store session token in localStorage for cross-origin requests
      if (data.session_token) {
        setSessionToken(data.session_token);
      }

      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Email/Password registration
  const register = async (email, password, name, goals = [], professionalData = {}) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email, 
          password, 
          name, 
          goals,
          ...professionalData 
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Store session token in localStorage for cross-origin requests
      if (data.session_token) {
        setSessionToken(data.session_token);
      }

      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Process OAuth callback (legacy - for Emergent auth)
  const processOAuthCallback = async (sessionId) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'OAuth authentication failed');
      }

      // Store session token in localStorage for cross-origin requests
      if (data.session_token) {
        setSessionToken(data.session_token);
      }

      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update user profile (including goals)
  const updateProfile = async (updates) => {
    setError(null);
    try {
      let sessionToken = getSessionToken();
      
      // Log auth state for debugging
      console.log('updateProfile - Session token present:', !!sessionToken);
      console.log('updateProfile - Session token value:', sessionToken ? sessionToken.substring(0, 10) + '...' : 'none');
      
      // If no session token, try to refresh first
      if (!sessionToken) {
        console.log('updateProfile - No token, attempting refresh first...');
        const refreshed = await refreshSessionToken();
        if (refreshed) {
          sessionToken = getSessionToken();
          console.log('updateProfile - Token refreshed:', !!sessionToken);
        }
      }
      
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      } else {
        console.warn('updateProfile - No session token available after refresh attempt!');
      }
      
      console.log('Updating profile with:', updates);
      console.log('Request headers:', Object.keys(headers));
      
      let response;
      try {
        response = await fetch(`${API_URL}/api/auth/me`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(updates)
        });
      } catch (networkErr) {
        console.error('Network error during profile update:', networkErr);
        throw new Error('Network error: Please check your internet connection and try again.');
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error('Failed to parse response:', parseErr);
        throw new Error('Server returned an invalid response. Please try again.');
      }
      
      console.log('Profile update response status:', response.status);
      console.log('Profile update response:', data);
      
      if (!response.ok) {
        // If we get a 401, the user might not exist in backend - try to sync them
        if (response.status === 401 && user) {
          console.log('Got 401, user might not exist in backend. Attempting to sync user...');
          
          // First try to sync the user to the backend
          const syncResult = await syncUserToBackend(user);
          if (syncResult.success && syncResult.session_token) {
            console.log('User synced successfully, retrying update...');
            // Retry the update with the new token
            const retryResponse = await fetch(`${API_URL}/api/auth/me`, {
              method: 'PUT',
              headers: { 
                'Content-Type': 'application/json',
                'X-Session-Token': syncResult.session_token 
              },
              credentials: 'include',
              body: JSON.stringify(updates)
            });
            
            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              setUser(retryData);
              return { success: true, user: retryData };
            }
          }
          
          // If sync didn't work, try refreshing token as fallback
          console.log('Sync failed, attempting token refresh...');
          const refreshed = await refreshSessionToken();
          if (refreshed) {
            const newToken = getSessionToken();
            if (newToken) {
              console.log('Retrying with refreshed token...');
              const retryResponse = await fetch(`${API_URL}/api/auth/me`, {
                method: 'PUT',
                headers: { 
                  'Content-Type': 'application/json',
                  'X-Session-Token': newToken 
                },
                credentials: 'include',
                body: JSON.stringify(updates)
              });
              
              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                setUser(retryData);
                return { success: true, user: retryData };
              }
            }
          }
        }
        throw new Error(data.detail || 'Update failed');
      }

      setUser(data);
      return { success: true, user: data };
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      const sessionToken = getSessionToken();
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: sessionToken ? { 'X-Session-Token': sessionToken } : {}
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    clearSessionToken();
    setUser(null);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    loginWithGoogle,
    loginWithGoogleCredential,
    loginWithEmail,
    register,
    processOAuthCallback,
    syncUserToBackend,
    updateProfile,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
